import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Building, Users, Trophy, Landmark, School, Briefcase, Search, ArrowRight, Globe, MapPin, ExternalLink } from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  Federation: Landmark,
  Club: Trophy,
  Academy: School,
  Facility: Building,
  Corporate: Briefcase,
  default: Users
};

const SportsDirectoryPage = () => {
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [directoryRes, settingsRes] = await Promise.all([
          axios.get(`${API}/cms/directory`),
          axios.get(`${API}/cms/settings`)
        ]);
        setContent(directoryRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Failed to load directory content');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const { organizations, filters } = content || {};
  
  const filteredOrgs = organizations?.filter(org => {
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         org.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || org.type === selectedType;
    const matchesSport = selectedSport === 'all' || org.sport === selectedSport;
    const matchesCountry = selectedCountry === 'all' || org.country === selectedCountry;
    return matchesSearch && matchesType && matchesSport && matchesCountry;
  });

  // Group by type for categorized view
  const groupedByType = filteredOrgs?.reduce((acc, org) => {
    const type = org.type || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(org);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <MegaMenu />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-primary/10 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-primary/10 border border-primary/30 rounded-full text-primary text-sm font-medium mb-6">
              Sports Directory
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              African Sports<br />Organizations
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto">
              Explore federations, clubs, academies, facilities, and sports businesses 
              across Africa. Connect with the ecosystem.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-card-bg border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {filters?.types?.slice(0, 4).map((type) => {
              const count = organizations?.filter(o => o.type === type).length || 0;
              const IconComponent = typeIcons[type] || typeIcons.default;
              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(selectedType === type ? 'all' : type)}
                  className={`p-4 rounded-xl border transition-all ${
                    selectedType === type
                      ? 'bg-primary/10 border-primary/50'
                      : 'bg-background border-border hover:border-primary/30'
                  }`}
                  data-testid={`type-stat-${type}`}
                >
                  <IconComponent className={`w-6 h-6 mb-2 ${selectedType === type ? 'text-primary' : 'text-white/40'}`} />
                  <p className="text-2xl font-heading font-bold text-white">{count}</p>
                  <p className="text-white/60 text-sm">{type}s</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-background sticky top-16 lg:top-20 z-40 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-card-bg border border-border rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-primary"
                data-testid="directory-search"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-2.5 bg-card-bg border border-border rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                data-testid="type-filter"
              >
                <option value="all">All Types</option>
                {filters?.types?.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <select
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                className="px-4 py-2.5 bg-card-bg border border-border rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                data-testid="sport-filter"
              >
                <option value="all">All Sports</option>
                {filters?.sports?.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>

              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="px-4 py-2.5 bg-card-bg border border-border rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                data-testid="country-filter"
              >
                <option value="all">All Countries</option>
                {filters?.countries?.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Directory Listing */}
      <section className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {Object.entries(groupedByType || {}).map(([type, orgs]) => {
            const IconComponent = typeIcons[type] || typeIcons.default;
            return (
              <div key={type} className="mb-12 last:mb-0">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <IconComponent className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold text-white">{type}s</h2>
                  <span className="text-white/40 text-sm">({orgs.length})</span>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {orgs.map((org, idx) => (
                    <motion.div
                      key={org.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      viewport={{ once: true }}
                      className="group bg-card-bg border border-border rounded-xl p-4 hover:border-primary/50 transition-all"
                      data-testid={`org-card-${org.id}`}
                    >
                      {/* Logo/Avatar */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          {org.logo ? (
                            <img src={org.logo} alt={org.name} className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <span className="text-lg font-heading font-bold text-primary">
                              {org.name?.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-white truncate group-hover:text-primary transition-colors">
                            {org.name}
                          </h3>
                          <p className="text-white/50 text-xs">{org.type}</p>
                        </div>
                      </div>

                      {/* Meta */}
                      <div className="space-y-1.5 text-xs text-white/50">
                        {org.sport && (
                          <p className="flex items-center gap-2">
                            <Trophy size={12} className="text-primary" />
                            {org.sport}
                          </p>
                        )}
                        {(org.city || org.country) && (
                          <p className="flex items-center gap-2">
                            <MapPin size={12} />
                            {[org.city, org.country].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>

                      {/* Contact */}
                      {org.website && (
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                          <a href={org.website} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-white/5 rounded text-white/40 hover:text-primary hover:bg-primary/10 transition-colors">
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}

          {(!filteredOrgs || filteredOrgs.length === 0) && (
            <div className="text-center py-20">
              <Globe className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-bold text-white mb-2">No Organizations Found</h3>
              <p className="text-white/60">Try adjusting your search or filters.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/20 via-background to-secondary/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            List Your Organization
          </h2>
          <p className="text-xl text-white/70 mb-10">
            Join Africa's largest sports directory and connect with partners, 
            sponsors, and talent across the continent.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl transition-all"
            data-testid="list-org-cta"
          >
            Get Listed <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
};

export default SportsDirectoryPage;
